import { sellByWeightFromUnit } from '@shared/quantity';
import { getCatalogRamSnapshotCache } from './catalogRamSnapshotCache';
import type { Product } from '../domain/productTypes';
export type {
  BulkPreviewItem,
  CatalogFilter,
  CatalogProductRow,
  CatalogSort,
  ProductCatalogEventRow,
  ProductCatalogEventType,
  ProductCatalogHistoryFilters,
  ProductMutationOpts,
  ProductQueryParams,
  ProductSearchRow,
} from '../domain/catalogTypes';

export {
  normalizeProductCode,
  productCodeUniquenessKey,
  validateBarcode,
  validateSku,
} from '../domain/catalogValidation';

export {
  getBulkPreview,
  getBulkScopeCounts,
  bulkUpdatePricesByCoefficient,
  listDistinctProductBrandsLocal,
} from './productCatalogBulk';

export {
  createProduct,
  updateProduct,
  deactivateProduct,
  reactivateProduct,
  bulkDeactivateProducts,
  bulkReactivateProducts,
} from './productWriteRepo';

export { updateProductAvgCost } from './productCatalogCostEdit';

export const getProductById = async (productId: string) => {
  const cached = getCatalogRamSnapshotCache()?.products.find((p) => p.id === productId);
        if (!cached) return null;
        const row = cached;
        return {
          id: String(row.id),
          name: String(row.name ?? ''),
          price: Number(row.price ?? 0),
          sku: row.sku != null ? String(row.sku) : '',
          barcode: row.barcode != null ? String(row.barcode) : null,
          categoryId: row.category_id != null && String(row.category_id).trim() !== '' ? String(row.category_id).trim() : null,
          itemType: row.item_type === 'service' ? 'service' : 'physical',
          isMisc: row.is_misc === true,
          organizationId: row.organization_id != null ? String(row.organization_id) : null,
          isActive: row.is_active !== false,
          createdAt: String(row.created_at ?? ''),
          updatedAt: String(row.updated_at ?? ''),
          brandId: row.brand_id != null ? String(row.brand_id) : null,
          internalNotes: row.internal_notes != null ? String(row.internal_notes) : null,
          preferredSupplierId: row.preferred_supplier_id != null ? String(row.preferred_supplier_id) : null,
          supplierItemCode: row.supplier_item_code != null ? String(row.supplier_item_code) : null,
          taxClass: row.tax_class != null && String(row.tax_class).trim() !== '' ? String(row.tax_class).trim() : null,
          unitOfMeasure:
            row.unit_of_measure != null && String(row.unit_of_measure).trim() !== ''
              ? (String(row.unit_of_measure).trim() as Product['unitOfMeasure'])
              : 'unit',
          sellByWeight: sellByWeightFromUnit(row.unit_of_measure),
          durationMinutes:
            row.duration_minutes != null && Number.isFinite(Number(row.duration_minutes))
              ? Math.floor(Number(row.duration_minutes))
              : null,
          bufferBeforeMinutes:
            row.buffer_before_minutes != null && Number.isFinite(Number(row.buffer_before_minutes))
              ? Math.max(0, Math.floor(Number(row.buffer_before_minutes)))
              : 0,
          bufferAfterMinutes:
            row.buffer_after_minutes != null && Number.isFinite(Number(row.buffer_after_minutes))
              ? Math.max(0, Math.floor(Number(row.buffer_after_minutes)))
              : 0,
          imageUrl: row.image_url != null ? String(row.image_url) : null,
          videoUrl: row.video_url != null ? String(row.video_url) : null,
          publicDescription: row.public_description != null ? String(row.public_description) : null,
          minOrderQty:
            row.min_order_qty != null && Number.isFinite(Number(row.min_order_qty))
              ? Number(row.min_order_qty)
              : null,
          maxOrderQty:
            row.max_order_qty != null && Number.isFinite(Number(row.max_order_qty))
              ? Number(row.max_order_qty)
              : null,
          isPublic: row.is_public !== false,
          activeIngredient: row.active_ingredient != null ? String(row.active_ingredient) : null,
          usageTags: Array.isArray(row.usage_tags) ? row.usage_tags.map(String) : [],
          allergens: Array.isArray((row as { allergens?: unknown }).allergens)
            ? ((row as { allergens: unknown[] }).allergens).map(String)
            : [],
          bookingMode: row.booking_mode === 'quote_required' ? 'quote_required' : 'direct',
          evaluationProductId: row.evaluation_product_id != null ? String(row.evaluation_product_id) : null,
          bookingPaymentMode:
            row.booking_payment_mode === 'none' ||
            row.booking_payment_mode === 'deposit' ||
            row.booking_payment_mode === 'full'
              ? row.booking_payment_mode
              : 'inherit',
          bookingDepositAmount:
            row.booking_deposit_amount != null && Number.isFinite(Number(row.booking_deposit_amount))
              ? Number(row.booking_deposit_amount)
              : null,
          bookingDepositPercent:
            row.booking_deposit_percent != null && Number.isFinite(Number(row.booking_deposit_percent))
              ? Number(row.booking_deposit_percent)
              : null,
          kitchenDestination:
            row.kitchen_destination === 'bar' ? 'bar' :
            row.kitchen_destination === 'none' ? 'none' :
            'kitchen',
          prepMinutes:
            row.prep_minutes != null && Number.isFinite(Number(row.prep_minutes))
              ? Number(row.prep_minutes)
              : null,
          menuAvailable: (row as { menu_available?: boolean }).menu_available !== false,
          visibleOnMenuCarta:
            (row as { visible_on_menu_carta?: boolean }).visible_on_menu_carta !== false,
          comboValidFrom:
            (row as { combo_valid_from?: string | null }).combo_valid_from ?? null,
          comboValidUntil:
            (row as { combo_valid_until?: string | null }).combo_valid_until ?? null,
        } satisfies Product & { taxClass: string | null };
};
